import React, { forwardRef, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RIGHT_SIDEBAR_ATTR, registerVisibleRightSidebar } from '@/lib/utils';
import { ChevronLeft, X } from 'lucide-react';

interface RightSidebarProps {
  title: string;
  isVisible: boolean;
  onClose: () => void;
  onBack?: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}

// The app shell renders a 56px-tall sticky `<TopNavbar>` (h-14) at the top of
// every page (`SidebarLayout` + `TopNavbar`). The right sidebar must sit
// BELOW that navbar — otherwise it covers brand/nav/search and looks "out of
// bounds." Anchoring with `top-14` + `bottom-0` (no explicit height) keeps it
// pinned to the visible main area on every screen height.
const NAVBAR_OFFSET = 'top-14';

export const RightSidebar = forwardRef<HTMLDivElement, RightSidebarProps>(
  ({ title, isVisible, onClose, onBack, children, footer, width = 'w-[450px]' }, ref) => {
    const { t } = useTranslation();
    // Depth is claimed on open, so a sidebar opened from another sidebar
    // stacks above it. Every panel otherwise shares one z-index and DOM order
    // decides — which puts app-root-mounted panels behind route-level ones.
    const [depth, setDepth] = useState(0);
    useEffect(() => {
      if (!isVisible) return;
      const { depth: claimed, release } = registerVisibleRightSidebar();
      setDepth(claimed);
      return release;
    }, [isVisible]);
    const overlayZ = 55 + depth * 10;
    const panelZ = overlayZ + 5;

    // Portal to body so the sidebar escapes any transformed ancestor (Radix
    // Dialog centers via `transform`, which creates a containing block for
    // `position: fixed` descendants — without portaling, the sidebar would
    // anchor to the dialog box and visibly deform). z-[55]/z-[60] sit above
    // shadcn Dialog/Sheet (z-50).
    //
    // Radix's floating poppers (Select, DropdownMenu, Popover, ContextMenu,
    // Menubar, HoverCard) must stay ABOVE this stack, so they carry z-[100].
    // They used to share Dialog's z-50 and rendered *behind* the panel: a
    // dropdown inside a side panel opened correctly — options in the DOM,
    // aria-expanded true — but was painted underneath it, so it read as "the
    // dropdown does nothing". Radix copies the content's computed z-index onto
    // `[data-radix-popper-content-wrapper]`, so the class on the content is
    // what actually decides the stacking. If this stack ever goes above 100,
    // raise the poppers too. `pointer-events-auto` on the panel is
    // required because those primitives set `body { pointer-events: none }`
    // while open; the portaled panel would otherwise let clicks fall through
    // to the backdrop and close the sidebar.
    return createPortal(
      <>
        <div
          {...{ [RIGHT_SIDEBAR_ATTR]: '' }}
          className={`fixed ${NAVBAR_OFFSET} left-0 right-0 bottom-0 bg-black/20 transition-opacity duration-300 ${
            isVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          style={{ zIndex: overlayZ }}
          onClick={onClose}
        />

        <div
          ref={ref}
          {...{ [RIGHT_SIDEBAR_ATTR]: '' }}
          className={`${width} max-w-full border-l border-border bg-card flex flex-col fixed ${NAVBAR_OFFSET} bottom-0 right-0 shadow-xl overflow-hidden pointer-events-auto`}
          style={{
            zIndex: panelZ,
            transform: `translateX(${isVisible ? '0' : '100%'})`,
            transition: 'transform 300ms ease-in-out',
          }}
        >
          <Card className="flex-none border-none rounded-none">
            <CardHeader className="border-b">
              <div className="flex justify-between items-center">
                <div className="flex min-w-0 items-center gap-1">
                  {onBack && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onBack}
                      aria-label={t('common.back')}
                      className="flex-none"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                  )}
                  <CardTitle className="truncate">{title}</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="flex-none"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* `min-h-0` lets the ScrollArea actually scroll inside the flex
              column. `min-h-full` on the wrapper means children with
              `h-full` (e.g. centered empty states) resolve to the visible
              viewport height; lists still grow beyond it and scroll. */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="w-full min-w-0 min-h-full">{children}</div>
          </ScrollArea>

          {footer && (
            <Card className="flex-none border-none rounded-none border-t">
              <CardContent className="p-4">{footer}</CardContent>
            </Card>
          )}
        </div>
      </>,
      document.body,
    );
  },
);

RightSidebar.displayName = 'RightSidebar';

export default RightSidebar;